import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { infiniteListPageSize } from "source/models/common/webConsts";
import * as recordService from "source/models/services/recordService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { IListUsingComponentState, List } from "source/components/framework/List/List";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { RecordFamiliarizationDialog } from "../RecordFamiliarizationDialog";
import { IRecordFamiliarizationListItem } from "ui/Scripts/source/models/recordFamiliarization/recordFamiliarizationListItem";

export interface IRecordFamiliarizationListProp {
    companyId: string;
}

interface IRecordFamiliarizationListState extends IListUsingComponentState<IRecordFamiliarizationListItem> {
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    checkedIds: string[];
}

export class RecordFamiliarizationList extends React.Component<IRecordFamiliarizationListProp, IRecordFamiliarizationListState> {
    constructor(props: IRecordFamiliarizationListProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: "code",
            sortOrderIsAsc: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => recordService.getRecordFamiliarizationListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setEditDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        });
    };

    refreshList = async (setEducationDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IRecordFamiliarizationListProp, prevState: IRecordFamiliarizationListState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };

    handleAdd = () => {
        this.setState(List.getShowEditDialogStateChange(this.state, Base.emptyGuid));
    };

    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleEditDialogOk = () => {
        this.refreshList(false);
    };

    handleEditDialogCancel = (refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshList) return;
        this.refreshList(false);
    };

    getRemoveSaveData = (props: IRecordFamiliarizationListProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("ids", ids);
        return result;
    };

    remove = async (ids: string[]): Promise<ApiSuccess> => {
        const props = this.props;
        if (ids.length < 1) return;
        const saveData = this.getRemoveSaveData(props, ids);
        // Call server
        const result = await AppUtils.callService(() => recordService.removeRecordFamiliarizations(saveData.obj));
        if (!result) return null;
        this.refreshList();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedRecordFamiliarizations
            : String.format(Translations.DoYouReallyWantToRemoveRecordFamiliarizationParameter, selectedItems[0].code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            this.remove(selectedIds);
        }
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.companyId ? Translations.Familiarizations : Translations.DefaultFamiliarizations}
                    info={props.companyId ? Translations.RecordUserModuleInfoFamiliarizations : undefined}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddFamiliarization}
                                    removeTooltip={Translations.RemoveFamiliarization}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                    }
                    content={
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={state.hasMore}
                            globalLoadingInUse={true}
                            multiSelect={true}
                            height="100%"
                            header={{
                                columns: [
                                    { size: 4, column: "code", content: Translations.Name },
                                    { size: 8, column: "comment", content: Translations.Description }
                                ]
                            }}
                            lines={state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 4,
                                            column: "code",
                                            content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                        },
                                        { size: 8, column: "comments", content: item.comment }
                                    ],
                                    id: item.id,
                                };
                            })}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenLine}
                        />
                    }
                />
                {(state.showEditDialog && !!state.editDialogId) &&
                    <RecordFamiliarizationDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleEditDialogOk}
                        onClose={this.handleEditDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}